





























































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import DaysComparatorChart, {
  ComparatorChartFilter,
} from "@/components/Investor/DaysComparatorChart.vue";
import SelectOption from "@/interfaces/SelectOption";
import VMultiSwitcher from "@/components/VMultiSwitcher.vue";
import VSwitchButton from "@/components/VSwitchButton.vue";
import VCheckButton from "@/components/VCheckButton.vue";
import HourInlineComparisonHelper from "@/services/HourInlineComparisonHelper";
import NamedWithTimeIntervals from "@/models/interfaces/NamedWithTimeIntervals";
import MultiLevelSelect from "@/components/MultiLevelSelect.vue";
import PieChart from "@/components/PieChart.vue";
import { CustomChartItem } from "@/components/CustomChart.vue";
import NoCache from "@/decorators/NoCache";
import { Getter } from "vuex-class";
import { AdcontrolProfile } from "@/models";

Component.registerHooks(["beforeRouteUpdate"]);

const vodBreakdownRequestTag = "vod_breakdown";

// eslint-disable-next-line
type IFormData = {
  granularity: string;
  filter: {
    media?: string[];
    spot?: string[];
    vod?: boolean;
    date_range: [string, string];
    product?: string;
    company?: string;
    brand?: string;
  };
};

@Component({
  components: {
    DaysComparatorChart,
    VMultiSwitcher,
    VSwitchButton,
    VCheckButton,
    MultiLevelSelect,
    PieChart,
  },
})
export default class VodComparationsCard extends Vue {
  @Prop() readonly spotIds?: string[];
  @Prop({ required: true }) readonly entity!: { id: string; type: string };
  @Prop({ required: true }) readonly filter!: ComparatorChartFilter;
  @Prop({ required: true }) readonly getData!: (
    data: IFormData,
    tag: string
  ) => Promise<NamedWithTimeIntervals[]>;
  @Prop({ required: true }) readonly adcontrolProfile!: AdcontrolProfile;

  private loading = false;
  weekdaysItems: NamedWithTimeIntervals[] = [];
  pieItems: CustomChartItem[] = [];

  media = { radio: false, tv: true };
  unitOfMeasure = "vod";

  charts = {
    weekdays: {
      percentage: false,
      type: "bar",
      stacked: false,
    },
    hours: {
      percentage: false,
      type: "line",
      stacked: false,
    },
  };

  settings = {
    unitOfMeasure: this.unitOfMeasure,
  };

  mounted() {
    this.settings.unitOfMeasure = this.unitOfMeasure;
    this.loadComparisonsInline();
  }

  @Watch("filter", { deep: true })
  onDaysComparationsFilter() {
    this.loadComparisonsInline();
  }

  @Watch("unitOfMeasure")
  onUnitChanged(value: string) {
    this.settings.unitOfMeasure = value;
  }

  @Watch("adcontrolProfile")
  onProfileChanged() {
    this.loadComparisonsInline();
  }

  async loadComparisonsInline() {
    const { type, id } = this.entity;
    this.loading = true;
    const date = this.filter.date;

    const data: IFormData = {
      filter: { media: ["2"], date_range: [date.from, date.to], vod: true },
      granularity: "wday",
    };

    if (type && id) {
      switch (type) {
        case "companies":
          data.filter.company = id;
          break;
        case "brands":
          data.filter.brand = id;
          break;
        case "products":
          data.filter.product = id;
          break;
      }
    }

    if (this.spotIds !== undefined) {
      data.filter.spot = this.spotIds;
    }

    const [_weekly, _daily] = await Promise.all([
      this.loadWeekdaysItems(data),
      this.loadPieItems(data),
    ]);

    this.loading = false;
  }

  async loadPieItems(data: IFormData) {
    const dayBroadcasters = await this.getBroadcasterDayData({
      ...data,
      granularity: "day",
    });
    const pieItems = dayBroadcasters.map((b) => {
      const total = b.timeIntervals.reduce(
        (acc, t) => acc + (t.vodDetection ?? 0),
        0
      );
      return { label: b.name, data: [total] };
    });
    this.pieItems = [...pieItems];
    return this.pieItems.some((b) => b.data?.[0] > 0);
  }

  async loadWeekdaysItems(data: IFormData) {
    const broadcasters = await this.getBroadcasterWeekData(data);
    const weekdaysItems = HourInlineComparisonHelper.ReduceData([
      ...broadcasters,
    ]);
    weekdaysItems.forEach((b) => {
      b.timeIntervals.sort((a, b) => {
        if (a.id < b.id) {
          return -1;
        }
        if (a.id > b.id) {
          return 1;
        }
        return 0;
      });
    });
    this.weekdaysItems = [...weekdaysItems];
  }

  getBroadcasterWeekData(data: IFormData) {
    return this.getData(data, vodBreakdownRequestTag);
  }

  getBroadcasterDayData(data: IFormData) {
    return this.getData(data, vodBreakdownRequestTag);
  }

  get weekdaysTVColor() {
    return "#f75151";
  }

  get weekdaysRadioColor() {
    return "#ff9d00";
  }

  @NoCache
  get anyVod() {
    return this.weekdaysItems.some((b) =>
      b.timeIntervals.some((t) => (t.vodDetection ?? 0) > 0)
    );
  }

  private chartTypeItems: Array<SelectOption> = [
    { id: "bar", text: "Barre" },
    { id: "line", text: "Linea" },
  ];
}
